import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/auth-store';
import {
  getInvoice,
  getInvoiceDocumentUrl,
  deleteInvoice,
  retryExtraction as reprocessInvoice,
  retryMatching,
  getTenantSettings,
  updateInvoice,
  unmatchTerm,
  unmatchCurrency,
  unmatchVendor,
  type InvoiceDetail as InvoiceDetailType,
  type TenantSettings,
} from '../services/api';
import {
  DEFAULT_LINE_ITEM_NUMERIC_PRECISION,
  getEffectiveValue,
  INVOICE_STATES,
  type InvoiceLineItem,
  type TermListItem,
  type PartyInfo,
  type VendorInfo,
  type InvoiceEdits,
} from '@acitra/shared';
import { useWebSocketSubscription } from '../hooks/useWebSocket';
import { DeleteInvoiceModal } from '../components/DeleteInvoiceModal';
import { ReprocessInvoiceModal } from '../components/ReprocessInvoiceModal';
import { RunMatchingModal } from '../components/RunMatchingModal';
import { VendorCard } from '../components/vendor-matching';
import { EditableValue } from '../components/EditableValue';
import { EditableDate } from '../components/EditableDate';
import { EditableTextarea } from '../components/EditableTextarea';
import { PaymentTermsSelector, CreateTermModal } from '../components/payment-terms';
import { CurrencySelector } from '../components/currency';
import { AddressModal } from '../components/AddressModal';
import { AddressCard, LineItemsSection, TotalsSection } from './InvoiceDetail/components';
import { ResetConfirmationModal } from '../components/ResetConfirmationModal';

// Helper to check if a field has been edited
function hasFieldChanged<T>(edited: T | null | undefined, original: T | undefined): boolean {
  if (edited === undefined) return false;
  // If explicitly cleared (null) and original had value, it changed
  if (edited === null) return original !== undefined && original !== null;
  // If set to a value, check if different from original
  return JSON.stringify(edited) !== JSON.stringify(original);
}

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  processing: 'bg-blue-100 text-blue-800',
  pending_approval: 'bg-purple-100 text-purple-800',
  approved: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-800',
  extraction_failed: 'bg-red-100 text-red-800',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  processing: 'Processing',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  rejected: 'Rejected',
  extraction_failed: 'Extraction Failed',
};

export function InvoiceDetail() {
  const navigate = useNavigate();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const { currentTenantId } = useAuthStore();

  const [invoice, setInvoice] = useState<InvoiceDetailType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [originalInvoiceDocumentUrl, setOriginalInvoiceDocumentUrl] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showReprocessModal, setShowReprocessModal] = useState(false);
  const [showMatchingModal, setShowMatchingModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isRunningMatching, setIsRunningMatching] = useState(false);
  const [tenantSettings, setTenantSettings] = useState<TenantSettings | null>(null);
  const [showCreateTermModal, setShowCreateTermModal] = useState(false);
  const [newlyCreatedTerm, setNewlyCreatedTerm] = useState<TermListItem | undefined>(undefined);

  // Address edit modals
  const [addressModalType, setAddressModalType] = useState<
    'billedTo' | 'shippedTo' | 'vendor' | null
  >(null);

  // Admin actions dropdown
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const adminMenuRef = useRef<HTMLDivElement>(null);

  // Fetch tenant settings for display precision
  useEffect(() => {
    if (!currentTenantId) return;

    getTenantSettings(currentTenantId)
      .then(setTenantSettings)
      .catch((err) => {
        console.error('Failed to fetch tenant settings:', err);
        // Don't fail the page, just use defaults
      });
  }, [currentTenantId]);

  const handleDelete = async () => {
    if (!currentTenantId || !invoiceId) return;
    await deleteInvoice(currentTenantId, invoiceId);
    navigate('/invoices');
  };

  const handleReprocess = async () => {
    if (!currentTenantId || !invoiceId) return;
    setIsReprocessing(true);
    try {
      await reprocessInvoice(currentTenantId, invoiceId);
      setShowReprocessModal(false);
      // WebSocket will notify us when processing completes
    } catch (err) {
      console.error('Reprocess failed:', err);
    } finally {
      setIsReprocessing(false);
    }
  };

  const handleReset = async () => {
    if (!currentTenantId || !invoiceId) return;
    setIsResetting(true);
    try {
      // Reset field edits
      const response = await updateInvoice(currentTenantId, invoiceId, {}, true);

      // Reset manual matches in parallel
      const resetPromises: Promise<void>[] = [];
      if (invoice?.paymentTermsMatch?.matchMethod === 'manual') {
        resetPromises.push(unmatchTerm(currentTenantId, invoiceId));
      }
      if (invoice?.currencyMatch?.matchMethod === 'manual') {
        resetPromises.push(unmatchCurrency(currentTenantId, invoiceId));
      }
      if (invoice?.vendorMatch?.matchMethod === 'manual') {
        resetPromises.push(unmatchVendor(currentTenantId, invoiceId));
      }
      await Promise.all(resetPromises);

      // Update local state
      setInvoice((prev) =>
        prev
          ? {
              ...prev,
              edits: response.edits,
              paymentTermsMatch:
                invoice?.paymentTermsMatch?.matchMethod === 'manual'
                  ? undefined
                  : prev.paymentTermsMatch,
              currencyMatch:
                invoice?.currencyMatch?.matchMethod === 'manual' ? undefined : prev.currencyMatch,
              vendorMatch:
                invoice?.vendorMatch?.matchMethod === 'manual' ? undefined : prev.vendorMatch,
            }
          : prev
      );

      setShowResetModal(false);
    } catch (err) {
      console.error('Reset failed:', err);
    } finally {
      setIsResetting(false);
    }
  };

  const handleRetryMatching = async () => {
    if (!currentTenantId || !invoiceId) return;
    setIsRunningMatching(true);
    try {
      await retryMatching(currentTenantId, invoiceId);
      setShowMatchingModal(false);
      // WebSocket will notify us when matching completes
    } catch (err) {
      console.error('Retry matching failed:', err);
    } finally {
      setIsRunningMatching(false);
    }
  };

  // Close admin menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (adminMenuRef.current && !adminMenuRef.current.contains(event.target as Node)) {
        setShowAdminMenu(false);
      }
    };

    if (showAdminMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showAdminMenu]);

  const fetchInvoice = useCallback(async () => {
    if (!currentTenantId || !invoiceId) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await getInvoice(currentTenantId, invoiceId);
      setInvoice(data);

      // Fetch document URL if the invoice has a document
      if (data.s3Key) {
        try {
          const { documentUrl: url } = await getInvoiceDocumentUrl(currentTenantId, invoiceId);
          setDocumentUrl(url);
        } catch (docErr) {
          console.error('Failed to fetch document URL:', docErr);
          // Don't fail the whole page if document URL fails
        }
      }

      // For duplicate invoices, also fetch the original invoice's document
      if (data.status === INVOICE_STATES.DUPLICATE_FILE && data.duplicateOfInvoiceId) {
        try {
          const { documentUrl: origUrl } = await getInvoiceDocumentUrl(
            currentTenantId,
            data.duplicateOfInvoiceId
          );
          setOriginalInvoiceDocumentUrl(origUrl);
        } catch (origDocErr) {
          console.error('Failed to fetch original invoice document URL:', origDocErr);
        }
      } else {
        setOriginalInvoiceDocumentUrl(null);
      }
    } catch (err) {
      console.error('Failed to fetch invoice:', err);
      setError(err instanceof Error ? err.message : 'Failed to load invoice');
    } finally {
      setIsLoading(false);
    }
  }, [currentTenantId, invoiceId]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  // Subscribe to real-time invoice updates via WebSocket
  useWebSocketSubscription(
    'invoice.updated',
    (data: { invoiceId: string }) => {
      // Only refresh if it's the current invoice
      if (data.invoiceId === invoiceId) {
        fetchInvoice();
      }
    },
    [invoiceId, fetchInvoice]
  );

  // Get precision from tenant settings (default: 2)
  const precision = tenantSettings?.lineItemNumericPrecision ?? DEFAULT_LINE_ITEM_NUMERIC_PRECISION;

  // Format quantity with tenant precision
  const formatQuantity = useMemo(
    () => (qty: number | undefined) => {
      if (qty === undefined) return '-';
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: precision,
      }).format(qty);
    },
    [precision]
  );

  // Format unit price with tenant precision
  const formatUnitPrice = useMemo(
    () => (price: number | undefined) => {
      if (price === undefined) return '-';
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      }).format(price);
    },
    [precision]
  );

  // Format amount without currency symbol (plain number with 2 decimals - always fixed for totals)
  const formatAmount = (amount: number | undefined) => {
    if (amount === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '-';
    // Handle YYYY-MM-DD format - return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    // Handle ISO timestamps (createdAt, updatedAt) - extract date portion
    return dateStr.split('T')[0];
  };

  // Handle line item edits - saves the entire array
  const handleLineItemEdit = async (
    idx: number,
    field: keyof InvoiceLineItem,
    value: string | number | null
  ) => {
    if (!currentTenantId || !invoiceId || !invoice) return;

    // Get current line items (from edits or original)
    const currentLineItems = invoice.edits?.lineItems ?? [...invoice.lineItems];

    // Update the specific field
    const updatedLineItems = currentLineItems.map((item, i) =>
      i === idx ? { ...item, [field]: value } : item
    );

    // Save the entire array
    const response = await updateInvoice(currentTenantId, invoiceId, {
      lineItems: updatedLineItems,
    });
    setInvoice((prev) => (prev ? { ...prev, edits: response.edits } : prev));
  };

  const handleLineItemDelete = async (idx: number) => {
    if (!currentTenantId || !invoiceId || !invoice) return;
    try {
      const currentLineItems = invoice.edits?.lineItems ?? [...invoice.lineItems];
      const updatedLineItems = currentLineItems.filter((_, i) => i !== idx);
      const response = await updateInvoice(currentTenantId, invoiceId, {
        lineItems: updatedLineItems,
      });
      setInvoice((prev) => (prev ? { ...prev, edits: response.edits } : prev));
    } catch (err) {
      console.error('Failed to delete line item:', err);
    }
  };

  const handleLineItemAdd = async () => {
    if (!currentTenantId || !invoiceId || !invoice) return;
    try {
      const currentLineItems = invoice.edits?.lineItems ?? [...invoice.lineItems];
      const newItem: InvoiceLineItem = {
        id: crypto.randomUUID(),
        description: '',
        quantity: undefined,
        unitPrice: undefined,
        amount: 0,
        itemCode: undefined,
        unitOfMeasure: undefined,
      };
      const updatedLineItems = [...currentLineItems, newItem];
      const response = await updateInvoice(currentTenantId, invoiceId, {
        lineItems: updatedLineItems,
      });
      setInvoice((prev) => (prev ? { ...prev, edits: response.edits } : prev));
    } catch (err) {
      console.error('Failed to add line item:', err);
    }
  };

  const handleAddressSave = async (data: PartyInfo | VendorInfo) => {
    if (!currentTenantId || !invoiceId || !invoice || !addressModalType) return;

    // Construct edits object based on what we're editing
    const edits: Partial<InvoiceEdits> = {};
    if (addressModalType === 'billedTo') {
      edits.billedTo = data;
    } else if (addressModalType === 'shippedTo') {
      edits.shippedTo = data;
    } else if (addressModalType === 'vendor') {
      edits.vendor = data as VendorInfo;
    }

    const response = await updateInvoice(currentTenantId, invoiceId, edits);
    setInvoice((prev) => (prev ? { ...prev, edits: response.edits } : prev));
  };

  const handleVendorEdit = () => {
    setAddressModalType('vendor');
  };

  if (isLoading) {
    return (
      <div className="p-8 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
        <button
          onClick={() => navigate('/invoices')}
          className="mt-4 text-primary-600 hover:text-primary-500"
        >
          Back to Invoices
        </button>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="p-8">
        <p className="text-gray-500">Invoice not found</p>
        <button
          onClick={() => navigate('/invoices')}
          className="mt-4 text-primary-600 hover:text-primary-500"
        >
          Back to Invoices
        </button>
      </div>
    );
  }

  // Check if there are any resettable changes (field edits or manual matches)
  const hasManualMatches =
    invoice.paymentTermsMatch?.matchMethod === 'manual' ||
    invoice.currencyMatch?.matchMethod === 'manual' ||
    invoice.vendorMatch?.matchMethod === 'manual';
  const hasEdits = invoice.edits && Object.keys(invoice.edits).length > 0;
  const canReset = hasEdits || hasManualMatches;

  return (
    <div className="p-8">
      {/* Reset Confirmation Modal */}
      <ResetConfirmationModal
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        onConfirm={handleReset}
        isResetting={isResetting}
      />

      {/* Address Edit Modal */}
      <AddressModal
        isOpen={!!addressModalType}
        onClose={() => setAddressModalType(null)}
        onSave={handleAddressSave}
        title={
          addressModalType === 'billedTo'
            ? 'Edit Billed To'
            : addressModalType === 'shippedTo'
              ? 'Edit Shipped To'
              : 'Edit Vendor'
        }
        initialData={
          addressModalType === 'billedTo'
            ? getEffectiveValue(invoice?.edits?.billedTo, invoice?.billedTo)
            : addressModalType === 'shippedTo'
              ? getEffectiveValue(invoice?.edits?.shippedTo, invoice?.shippedTo)
              : getEffectiveValue(invoice?.edits?.vendor, invoice?.vendor)
        }
        showContactInfo={addressModalType === 'vendor'}
      />

      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/invoices')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to Invoices
        </button>

        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Invoice{' '}
              {getEffectiveValue(invoice.edits?.invoiceNumber, invoice.invoiceNumber) ||
                invoice.id.slice(0, 8)}
            </h1>
            {invoice.originalFilename && (
              <p className="text-sm text-gray-400 mt-1">{invoice.originalFilename}</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span
              className={`px-3 py-1 text-sm font-semibold rounded-full ${
                STATUS_COLORS[invoice.status] || 'bg-gray-100 text-gray-800'
              }`}
            >
              {STATUS_LABELS[invoice.status] || invoice.status}
            </span>
            {invoice.status !== INVOICE_STATES.DUPLICATE_FILE && (
              <>
                {canReset && (
                  <button
                    onClick={() => setShowResetModal(true)}
                    className="px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <span className="flex items-center gap-1.5">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                        />
                      </svg>
                      Reset
                    </span>
                  </button>
                )}
              </>
            )}
            {/* Admin actions dropdown */}
            <div className="relative" ref={adminMenuRef}>
              <button
                onClick={() => setShowAdminMenu(!showAdminMenu)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                aria-label="More actions"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="5" r="2" />
                  <circle cx="12" cy="12" r="2" />
                  <circle cx="12" cy="19" r="2" />
                </svg>
              </button>
              {showAdminMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-10">
                  <div className="py-1">
                    {invoice.status !== INVOICE_STATES.DUPLICATE_FILE && (
                      <>
                        <button
                          onClick={() => {
                            setShowAdminMenu(false);
                            setShowReprocessModal(true);
                          }}
                          className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
                            />
                          </svg>
                          Reprocess
                        </button>
                        <button
                          onClick={() => {
                            setShowAdminMenu(false);
                            setShowMatchingModal(true);
                          }}
                          className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                            />
                          </svg>
                          Run Matching
                        </button>
                        <div className="border-t border-gray-100 my-1" />
                      </>
                    )}
                    <button
                      onClick={() => {
                        setShowAdminMenu(false);
                        setShowDeleteModal(true);
                      }}
                      className="w-full px-4 py-2 text-sm text-left text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Two-column layout: PDF viewer + Details */}
      <div className="flex gap-6">
        {/* Left column: PDF Viewer */}
        <div className="w-1/2 flex-shrink-0">
          <div className="bg-white shadow rounded-lg overflow-hidden sticky top-8">
            <div
              className={`px-4 py-3 border-b ${
                invoice.status === INVOICE_STATES.DUPLICATE_FILE
                  ? 'border-red-200 bg-red-50'
                  : 'border-gray-200 bg-gray-50'
              }`}
            >
              <h2
                className={`text-sm font-medium ${
                  invoice.status === INVOICE_STATES.DUPLICATE_FILE
                    ? 'text-red-700'
                    : 'text-gray-700'
                }`}
              >
                {invoice.status === INVOICE_STATES.DUPLICATE_FILE
                  ? 'Duplicate Document'
                  : 'Original Document'}
              </h2>
            </div>
            {documentUrl ? (
              <iframe
                src={documentUrl}
                className="w-full h-[calc(100vh-200px)] min-h-[600px]"
                title="Invoice PDF"
              />
            ) : (
              <div className="flex items-center justify-center h-[600px] bg-gray-50">
                <p className="text-sm text-gray-500">No document available</p>
              </div>
            )}
          </div>
        </div>

        {/* Right column: Invoice Details or Original Document (for duplicates) */}
        <div className="w-1/2 space-y-6">
          {invoice.status === INVOICE_STATES.DUPLICATE_FILE ? (
            /* For duplicates, show the original invoice's document */
            <div className="bg-white shadow rounded-lg overflow-hidden sticky top-8">
              <div className="px-4 py-3 border-b border-primary-200 bg-primary-50">
                <h2 className="text-sm font-medium text-primary-700">Original Document</h2>
              </div>
              {originalInvoiceDocumentUrl ? (
                <iframe
                  src={originalInvoiceDocumentUrl}
                  className="w-full h-[calc(100vh-200px)] min-h-[600px]"
                  title="Original Invoice PDF"
                />
              ) : (
                <div className="flex items-center justify-center h-[600px] bg-gray-50">
                  <p className="text-sm text-gray-500">
                    {invoice.duplicateOfInvoiceId
                      ? 'Loading original document...'
                      : 'Original document not available'}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Error messages */}
              {invoice.errors && invoice.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-red-800 mb-2">Processing Errors</h3>
                  <ul className="list-disc list-inside space-y-1">
                    {invoice.errors.map((err, idx) => (
                      <li key={idx} className="text-sm text-red-700">
                        {err.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Vendor matching */}
              <VendorCard
                tenantId={currentTenantId!}
                invoiceId={invoiceId!}
                extractedVendor={getEffectiveValue(invoice.edits?.vendor, invoice.vendor)}
                originalVendor={invoice.vendor}
                effectiveCurrency={
                  invoice.currencyMatch?.entityName?.split(' - ')[0] || invoice.currency
                }
                vendorMatch={invoice.vendorMatch}
                matchedVendorDetails={invoice.matchedVendorDetails}
                onMatchUpdated={fetchInvoice}
                onEdit={() => handleVendorEdit()}
              />

              {/* Invoice details */}
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Invoice Details</h2>
                <div className="grid grid-cols-2 gap-4">
                  {/* Left column: Invoice info */}
                  <div className="space-y-1">
                    {/* Invoice Number */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Invoice Number</span>
                      <EditableValue
                        value={
                          getEffectiveValue(invoice.edits?.invoiceNumber, invoice.invoiceNumber) ||
                          '-'
                        }
                        onSave={async (value) => {
                          if (!currentTenantId || !invoiceId) return;
                          const response = await updateInvoice(currentTenantId, invoiceId, {
                            invoiceNumber: value,
                          });
                          setInvoice((prev) => (prev ? { ...prev, edits: response.edits } : prev));
                        }}
                        width="w-40"
                        className={
                          hasFieldChanged(invoice.edits?.invoiceNumber, invoice.invoiceNumber)
                            ? '!text-edited-600'
                            : ''
                        }
                        originalValue={
                          invoice.edits?.invoiceNumber !== undefined
                            ? invoice.invoiceNumber || '-'
                            : undefined
                        }
                        onReset={
                          invoice.edits?.invoiceNumber !== undefined
                            ? async () => {
                                if (!currentTenantId || !invoiceId) return;
                                const response = await updateInvoice(currentTenantId, invoiceId, {
                                  invoiceNumber: null,
                                });
                                setInvoice((prev) =>
                                  prev ? { ...prev, edits: response.edits } : prev
                                );
                              }
                            : undefined
                        }
                      />
                    </div>

                    {/* PO Number */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">PO Number</span>
                      <EditableValue
                        value={
                          getEffectiveValue(
                            invoice.edits?.purchaseOrderNumber,
                            invoice.purchaseOrderNumber
                          ) || '-'
                        }
                        onSave={async (value) => {
                          if (!currentTenantId || !invoiceId) return;
                          const response = await updateInvoice(currentTenantId, invoiceId, {
                            purchaseOrderNumber: value,
                          });
                          setInvoice((prev) => (prev ? { ...prev, edits: response.edits } : prev));
                        }}
                        width="w-40"
                        className={
                          hasFieldChanged(
                            invoice.edits?.purchaseOrderNumber,
                            invoice.purchaseOrderNumber
                          )
                            ? '!text-edited-600'
                            : ''
                        }
                        originalValue={
                          invoice.edits?.purchaseOrderNumber !== undefined
                            ? invoice.purchaseOrderNumber || '-'
                            : undefined
                        }
                        onReset={
                          invoice.edits?.purchaseOrderNumber !== undefined
                            ? async () => {
                                if (!currentTenantId || !invoiceId) return;
                                const response = await updateInvoice(currentTenantId, invoiceId, {
                                  purchaseOrderNumber: null,
                                });
                                setInvoice((prev) =>
                                  prev ? { ...prev, edits: response.edits } : prev
                                );
                              }
                            : undefined
                        }
                      />
                    </div>

                    {/* Payment Terms */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1">
                        <span className="text-sm text-gray-500">Payment Terms</span>
                        {(invoice.paymentTerms || invoice.paymentTermsDueDays) && (
                          <span
                            className={
                              invoice.paymentTermsMatch ? 'text-emerald-500' : 'text-red-500'
                            }
                            title={invoice.paymentTermsMatch ? 'Matched' : 'Not matched'}
                          >
                            {invoice.paymentTermsMatch ? '✓' : '✗'}
                          </span>
                        )}
                      </div>
                      <div className="w-40">
                        {currentTenantId && invoiceId ? (
                          <PaymentTermsSelector
                            tenantId={currentTenantId}
                            invoiceId={invoiceId}
                            extractedTerms={{
                              paymentTerms: invoice.paymentTerms,
                              paymentTermsRaw: invoice.paymentTermsRaw,
                              paymentTermsDueDays: invoice.paymentTermsDueDays,
                              paymentTermsDiscountPercent: invoice.paymentTermsDiscountPercent,
                              paymentTermsDiscountDays: invoice.paymentTermsDiscountDays,
                            }}
                            currentMatch={invoice.paymentTermsMatch}
                            onMatchChange={(match) => {
                              setInvoice((prev) =>
                                prev ? { ...prev, paymentTermsMatch: match } : prev
                              );
                            }}
                            onOpenCreateModal={() => setShowCreateTermModal(true)}
                            newlyCreatedTerm={newlyCreatedTerm}
                            className={
                              invoice.paymentTermsMatch?.matchMethod === 'manual'
                                ? '!text-edited-600'
                                : ''
                            }
                          />
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                    </div>

                    {/* Currency */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1">
                        <span className="text-sm text-gray-500">Currency</span>
                        {invoice.currency && (
                          <span
                            className={invoice.currencyMatch ? 'text-emerald-500' : 'text-red-500'}
                            title={invoice.currencyMatch ? 'Matched' : 'Not matched'}
                          >
                            {invoice.currencyMatch ? '✓' : '✗'}
                          </span>
                        )}
                      </div>
                      <div className="w-40">
                        {currentTenantId && invoiceId ? (
                          <CurrencySelector
                            tenantId={currentTenantId}
                            invoiceId={invoiceId}
                            extractedCurrency={invoice.currency}
                            currentMatch={invoice.currencyMatch}
                            onMatchChange={(match) => {
                              setInvoice((prev) =>
                                prev ? { ...prev, currencyMatch: match } : prev
                              );
                            }}
                            className={
                              invoice.currencyMatch?.matchMethod === 'manual'
                                ? '!text-edited-600'
                                : ''
                            }
                          />
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right column: Dates */}
                  <div className="space-y-1">
                    {/* Received Date */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Received Date</span>
                      <span className="w-32 py-1.5 px-2 text-sm font-medium text-gray-700 text-right">
                        {formatDate(invoice.createdAt)}
                      </span>
                    </div>

                    {/* Invoice Date */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Invoice Date</span>
                      <EditableDate
                        value={getEffectiveValue(invoice.edits?.invoiceDate, invoice.invoiceDate)}
                        onSave={async (value) => {
                          if (!currentTenantId || !invoiceId) return;
                          const response = await updateInvoice(currentTenantId, invoiceId, {
                            invoiceDate: value,
                          });
                          setInvoice((prev) => (prev ? { ...prev, edits: response.edits } : prev));
                        }}
                        width="w-32"
                        className={
                          hasFieldChanged(invoice.edits?.invoiceDate, invoice.invoiceDate)
                            ? '!text-edited-600'
                            : ''
                        }
                        originalValue={
                          invoice.edits?.invoiceDate !== undefined ? invoice.invoiceDate : undefined
                        }
                        onReset={
                          invoice.edits?.invoiceDate !== undefined
                            ? async () => {
                                if (!currentTenantId || !invoiceId) return;
                                const response = await updateInvoice(currentTenantId, invoiceId, {
                                  invoiceDate: null,
                                });
                                setInvoice((prev) =>
                                  prev ? { ...prev, edits: response.edits } : prev
                                );
                              }
                            : undefined
                        }
                      />
                    </div>

                    {/* Due Date */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">
                        Due Date
                        {invoice.isDueDateCalculated && !invoice.edits?.dueDate && (
                          <span
                            className="ml-1 text-xs text-amber-600"
                            title="Calculated from payment terms"
                          >
                            (calc)
                          </span>
                        )}
                      </span>
                      <EditableDate
                        value={getEffectiveValue(invoice.edits?.dueDate, invoice.dueDate)}
                        onSave={async (value) => {
                          if (!currentTenantId || !invoiceId) return;
                          const response = await updateInvoice(currentTenantId, invoiceId, {
                            dueDate: value,
                          });
                          setInvoice((prev) => (prev ? { ...prev, edits: response.edits } : prev));
                        }}
                        width="w-32"
                        className={
                          hasFieldChanged(invoice.edits?.dueDate, invoice.dueDate)
                            ? '!text-edited-600'
                            : ''
                        }
                        originalValue={
                          invoice.edits?.dueDate !== undefined ? invoice.dueDate : undefined
                        }
                        onReset={
                          invoice.edits?.dueDate !== undefined
                            ? async () => {
                                if (!currentTenantId || !invoiceId) return;
                                const response = await updateInvoice(currentTenantId, invoiceId, {
                                  dueDate: null,
                                });
                                setInvoice((prev) =>
                                  prev ? { ...prev, edits: response.edits } : prev
                                );
                              }
                            : undefined
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Billed To / Shipped To */}
              {(invoice.billedTo ||
                invoice.shippedTo ||
                invoice.edits?.billedTo ||
                invoice.edits?.shippedTo) && (
                <div
                  className={`grid gap-4 ${
                    (invoice.billedTo || invoice.edits?.billedTo) &&
                    (invoice.shippedTo || invoice.edits?.shippedTo)
                      ? 'grid-cols-2'
                      : 'grid-cols-1'
                  }`}
                >
                  {(invoice.billedTo || invoice.edits?.billedTo) && (
                    <AddressCard
                      title="Billed To"
                      effective={getEffectiveValue(invoice.edits?.billedTo, invoice.billedTo)}
                      original={invoice.billedTo}
                      onEdit={() => setAddressModalType('billedTo')}
                    />
                  )}
                  {(invoice.shippedTo || invoice.edits?.shippedTo) && (
                    <AddressCard
                      title="Shipped To"
                      effective={getEffectiveValue(invoice.edits?.shippedTo, invoice.shippedTo)}
                      original={invoice.shippedTo}
                      onEdit={() => setAddressModalType('shippedTo')}
                    />
                  )}
                </div>
              )}

              {/* Line items */}
              <LineItemsSection
                lineItems={invoice.edits?.lineItems ?? invoice.lineItems}
                originalLineItems={invoice.lineItems}
                precision={precision}
                formatQuantity={formatQuantity}
                formatUnitPrice={formatUnitPrice}
                formatAmount={formatAmount}
                onLineItemEdit={handleLineItemEdit}
                onLineItemDelete={handleLineItemDelete}
                onLineItemAdd={handleLineItemAdd}
              />

              {/* Totals */}
              <TotalsSection
                lineItems={invoice.edits?.lineItems ?? invoice.lineItems ?? []}
                subtotal={invoice.subtotal}
                editedSubtotal={invoice.edits?.subtotal}
                taxes={invoice.taxes}
                total={invoice.total}
                editedTotal={invoice.edits?.total}
                currency={invoice.currency}
                currencyMatch={invoice.currencyMatch}
                formatAmount={formatAmount}
                onSubtotalEdit={async (value) => {
                  if (!currentTenantId || !invoiceId || value === null) return;

                  const parsed = parseFloat(value.replace(/,/g, ''));
                  if (!isNaN(parsed)) {
                    const response = await updateInvoice(currentTenantId, invoiceId, {
                      subtotal: parsed,
                    });
                    setInvoice((prev) => (prev ? { ...prev, edits: response.edits } : prev));
                  }
                }}
                onTotalEdit={async (value) => {
                  if (!currentTenantId || !invoiceId || value === null) return;

                  const parsed = parseFloat(value.replace(/,/g, ''));
                  if (!isNaN(parsed)) {
                    const response = await updateInvoice(currentTenantId, invoiceId, {
                      total: parsed,
                    });
                    setInvoice((prev) => (prev ? { ...prev, edits: response.edits } : prev));
                  }
                }}
                onSubtotalReset={async () => {
                  if (!currentTenantId || !invoiceId) return;
                  const response = await updateInvoice(currentTenantId, invoiceId, {
                    subtotal: null,
                  });
                  setInvoice((prev) => (prev ? { ...prev, edits: response.edits } : prev));
                }}
                onTotalReset={async () => {
                  if (!currentTenantId || !invoiceId) return;
                  const response = await updateInvoice(currentTenantId, invoiceId, {
                    total: null,
                  });
                  setInvoice((prev) => (prev ? { ...prev, edits: response.edits } : prev));
                }}
              />

              {/* Notes */}
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-2">Notes</h2>
                <EditableTextarea
                  value={getEffectiveValue(invoice.edits?.notes, invoice.notes)}
                  onSave={async (value) => {
                    if (!currentTenantId || !invoiceId) return;
                    const response = await updateInvoice(currentTenantId, invoiceId, {
                      notes: value,
                    });
                    setInvoice((prev) => (prev ? { ...prev, edits: response.edits } : prev));
                  }}
                  placeholder="Click to add notes..."
                  minRows={3}
                />
              </div>

              {/* Confidence score & Token usage */}
              {(invoice.confidence !== undefined ||
                invoice.isInvoiceConfidence !== undefined ||
                invoice.tokenUsage) && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                  {invoice.isInvoiceConfidence !== undefined && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Document Type Confidence</span>
                      <span
                        className={`text-sm font-medium ${
                          invoice.isInvoiceConfidence >= 0.8
                            ? 'text-green-600'
                            : invoice.isInvoiceConfidence >= 0.6
                              ? 'text-yellow-600'
                              : 'text-red-600'
                        }`}
                      >
                        {Math.round(invoice.isInvoiceConfidence * 100)}% invoice
                      </span>
                    </div>
                  )}
                  {invoice.confidence !== undefined && (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Extraction Confidence</span>
                        <span
                          className={`text-sm font-medium ${
                            invoice.confidence >= 0.8
                              ? 'text-green-600'
                              : invoice.confidence >= 0.6
                                ? 'text-yellow-600'
                                : 'text-red-600'
                          }`}
                        >
                          {Math.round(invoice.confidence * 100)}%
                        </span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            invoice.confidence >= 0.8
                              ? 'bg-green-500'
                              : invoice.confidence >= 0.6
                                ? 'bg-yellow-500'
                                : 'bg-red-500'
                          }`}
                          style={{ width: `${invoice.confidence * 100}%` }}
                        />
                      </div>
                    </>
                  )}
                  {invoice.tokenUsage && (
                    <div className="pt-2 border-t border-gray-200 space-y-1">
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>Model</span>
                        <span className="font-mono">{invoice.tokenUsage.model}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>Tokens</span>
                        <span className="font-mono">
                          {invoice.tokenUsage.inputTokens.toLocaleString()} in /{' '}
                          {invoice.tokenUsage.outputTokens.toLocaleString()} out
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>Estimated Cost</span>
                        <span className="font-mono">
                          ${invoice.tokenUsage.estimatedCostUsd.toFixed(4)}
                        </span>
                      </div>
                      {invoice.rotationApplied !== undefined && (
                        <div className="flex flex-col gap-0.5 text-xs text-gray-500">
                          <div className="flex items-center justify-between">
                            <span>Document orientation</span>
                            <span className="font-mono">
                              {invoice.rotationApplied === 0
                                ? 'Correct (no rotation needed)'
                                : invoice.rotationApplied === 90
                                  ? '90° CCW → rotated 90° CW'
                                  : invoice.rotationApplied === 180
                                    ? 'Upside down → rotated 180°'
                                    : '90° CW → rotated 90° CCW'}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteInvoiceModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        invoiceNumber={invoice.invoiceNumber || invoice.id.slice(0, 8)}
      />

      {/* Reprocess Confirmation Modal */}
      <ReprocessInvoiceModal
        isOpen={showReprocessModal}
        onClose={() => setShowReprocessModal(false)}
        onConfirm={handleReprocess}
        invoiceNumber={invoice.invoiceNumber || invoice.id.slice(0, 8)}
        isProcessing={isReprocessing}
      />

      {/* Run Matching Confirmation Modal */}
      <RunMatchingModal
        isOpen={showMatchingModal}
        onClose={() => setShowMatchingModal(false)}
        onConfirm={handleRetryMatching}
        invoiceNumber={invoice.invoiceNumber || invoice.id.slice(0, 8)}
        isProcessing={isRunningMatching}
      />

      {/* Create Payment Term Modal */}
      {currentTenantId && (
        <CreateTermModal
          tenantId={currentTenantId}
          isOpen={showCreateTermModal}
          onClose={() => setShowCreateTermModal(false)}
          onTermCreated={(term) => {
            // Add newly created term optimistically so it appears in the selector immediately
            setNewlyCreatedTerm(term);
            setShowCreateTermModal(false);
          }}
          initialValues={{
            dueDays: invoice.paymentTermsDueDays,
            discountPercent: invoice.paymentTermsDiscountPercent,
            discountDays: invoice.paymentTermsDiscountDays,
          }}
        />
      )}
    </div>
  );
}
